
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import AdBanner from "../components/AdBanner";
import { adsAPI } from "../services/api";
import { CATEGORIES } from "../utils/helpers";

export default function Catalog() {
  const { products, currentUser } = useApp();
  const [search, setSearch]     = useState("");
  const [cat, setCat]           = useState("Todos");
  const [showSold, setShowSold] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1100);
  const [ads, setAds]           = useState([]);
  const [mobileAdOffset, setMobileAdOffset] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1100);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    adsAPI.getActive().then(({ ads }) => setAds(ads || [])).catch(() => {});
  }, []);

  // En mobile, si hay mas publicidades que espacios, van rotando cada 5s
  useEffect(() => {
    if (ads.length <= 1) return;
    const interval = setInterval(() => setMobileAdOffset(prev => (prev + 1) % ads.length), 5000);
    return () => clearInterval(interval);
  }, [ads]);

  // Reparte todas las publicidades activas entre las dos columnas, apiladas
  const half     = Math.ceil(ads.length / 2);
  const leftAds  = ads.slice(0, half);
  const rightAds = ads.slice(half);
  const hasAds   = ads.length > 0;
  const showAds  = hasAds && !!currentUser; // los que no iniciaron sesion solo ven productos, sin publicidades

  const filtered = products.filter(p => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase()) ||
                        p.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat  = cat === "Todos" || p.category === cat;
    const matchSold = showSold ? true : !p.sold;
    return matchSearch && matchCat && matchSold;
  });

  // Layout dinámico según si hay ads
  const layoutStyle = isMobile
    ? s.layoutMobile
    : showAds
      ? s.layoutWithAds
      : s.layoutNoAds;

  return (
    <div style={s.page}>
      <div style={{...s.layout, ...layoutStyle}}>

        {/* LEFT ADS — solo desktop con ads, y solo si hay sesion iniciada */}
        {!isMobile && showAds && leftAds.length > 0 && (
          <aside style={s.sidebar}>
            {leftAds.map(ad => <AdBanner key={ad.id} ad={ad} />)}
          </aside>
        )}

        {/* MAIN */}
        <main style={s.main}>
          <h1 style={s.heading}>Catalogo</h1>

          {!currentUser && (
            <p style={s.loginNotice}>
              🔒 Para comprar o vender productos necesitas <Link to="/login" style={s.loginNoticeLink}>iniciar sesion</Link> o <Link to="/register" style={s.loginNoticeLink}>registrarte</Link>.
            </p>
          )}

          <input
            style={s.search}
            placeholder="🔍 Buscar producto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div style={{...s.pillsWrap, overflowX: isMobile ? "auto" : "hidden"}}>
            <div style={{...s.pills, width: isMobile ? "max-content" : "100%", flexWrap: isMobile ? "nowrap" : "wrap"}}>
              {["Todos", ...CATEGORIES].map(c => (
                <button key={c} style={{...s.pill,...(cat===c?s.pillActive:{})}} onClick={() => setCat(c)}>{c}</button>
              ))}
            </div>
          </div>

          <div style={s.filterRow}>
            <p style={s.count}>{filtered.length} producto{filtered.length!==1?"s":""}</p>
            <label style={s.checkLabel}>
              <input type="checkbox" checked={showSold} onChange={e => setShowSold(e.target.checked)} />
              &nbsp;Ver agotados
            </label>
          </div>

          {filtered.length === 0 ? (
            <div style={s.empty}>
              <span style={{fontSize:48}}>🔍</span>
              <p>No encontramos productos.</p>
            </div>
          ) : (
            <div style={{...s.grid, ...(isMobile ? s.gridMobile : {})}}>
              {filtered.map((p, i) => (
                <React.Fragment key={p.id}>
                  <ProductCard product={p} />
                  {isMobile && showAds && (i + 1) % 4 === 0 && (
                    <div style={s.mobileAd}>
                      <AdBanner layout="banner" ad={ads[(Math.floor(i / 4) + mobileAdOffset) % ads.length]} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </main>

        {/* RIGHT ADS — solo desktop con 2+ ads, y solo si hay sesion iniciada */}
        {!isMobile && showAds && rightAds.length > 0 && (
          <aside style={s.sidebar}>
            {rightAds.map(ad => <AdBanner key={ad.id} ad={ad} />)}
          </aside>
        )}
      </div>
    </div>
  );
}

const s = {
  page:{width:"100%",padding:"20px 16px "},
  layout:{display:"grid",alignItems:"center",maxWidth:1400,margin:"0 auto"},
  layoutNoAds:{gridTemplateColumns:"1fr"},
  layoutWithAds:{gridTemplateColumns:"220px 1fr 220px",gap:13},
  layoutMobile:{gridTemplateColumns:"1fr"},
  sidebar:{minWidth:0,paddingTop:0,display:"flex",flexDirection:"column",gap:16,position:"sticky",top:90,alignSelf:"start"},
  main:{minWidth:0},
  heading:{fontSize:22,fontWeight:700,marginBottom:18,color:"#111"},
  loginNotice:{background:"#fff8e1",color:"#8a6410",border:"0.5px solid #f5dfa0",borderRadius:10,padding:"10px 14px",fontSize:13,marginBottom:16},
  loginNoticeLink:{color:"#1D9E75",fontWeight:600,textDecoration:"none"},
  search:{width:"30%",height:46,padding:"0 16px",borderRadius:14,border:"1px solid #e4e4e4",fontSize:15,marginBottom:14,outline:"none",background:"#fff"},
  pillsWrap:{marginBottom:14,paddingBottom:4},
  pills:{display:"flex",gap:8},
  pill:{padding:"6px 12px",borderRadius:999,border:"1px solid #e5e5e5",background:"#fff",cursor:"pointer",fontSize:13,color:"#555",whiteSpace:"nowrap"},
  pillActive:{background:"#1D9E75",color:"#fff",borderColor:"#1D9E75"},
  filterRow:{display:"flex",justifyContent:"space-between",marginBottom:18,gap:12,flexWrap:"wrap"},
  count:{fontSize:13,color:"#888"},
  checkLabel:{fontSize:13,color:"#666",cursor:"pointer",display:"flex",alignItems:"center",gap:4},
  grid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:18},
  gridMobile:{gridTemplateColumns:"1fr 1fr",gap:10},
  mobileAd:{gridColumn:"1/-1"},
  empty:{textAlign:"center",padding:"70px 0",color:"#888",display:"flex",flexDirection:"column",alignItems:"center",gap:12},
};
